import { Inject, Injectable } from '@angular/core';
import { Query } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LibConfig } from '@challenger/care/models/lib-config.model';
import { LIB_CONFIG } from '@challenger/care/lib-config';
import gql from 'graphql-tag';
import { FaultArticle } from '@challenger/care/modules/graphql/generated/graphql-types';
import { NetworkStatus } from '@apollo/client';
import { ANALYTICS } from './care-analytics.service';
import { of } from 'rxjs';
import { CampaignContentService } from './campaign-content.service';

interface FaultArticlesResponse {
  data: {
    faultArticles: FaultArticle[];
  };
  loading: boolean;
  networkStatus: NetworkStatus;
}
export const FAULT_ARTICLE_QUERY = gql`
  query FaultArticleQuery($uid: String!, $publicationState: PublicationState!) {
    faultArticles(where: { uid: $uid }, publicationState: $publicationState) {
      metaTagSection {
        name
        content
      }
      uid
      titleOfTheBrowser
      articleTemplate {
        id
        heading1
        icon {
          id
          url
        }
        breadcrumbs
        pageContent
      }
      elements {
        ... on ComponentElementsGlobalComponent {
          genericComponent {
            type
            uid
            title
            subtitle
            copy
            icon {
              url
              webPURL
            }
            ctas {
              Uid
              label
              url
              targetNewPage
              alt
              contentUid
              targetList
              behavior
              dataAttr
              dataAttrValue
              icon {
                url
                webPURL
              }
            }
            listOfContent {
              text
              content
            }
            fa_icon_name
            fa_icon_weight
            fa_icon_color
            listOfLinks {
              links {
                label
                icon {
                  url
                  webPURL
                }
                url
                alt
                contentUid
              }
            }
            fa_icon_name
            fa_icon_color
            fa_icon_weight
            externalUrl
          }
        }
        ... on ComponentElementsGenericComponent {
          type
          title
          subtitle
          copy
          icon {
            url
            webPURL
          }
          ctas {
            label
            url
            targetNewPage
            alt
            contentUid
            targetList
            behavior
            dataAttr
            dataAttrValue
            icon {
              url
              webPURL
            }
          }
          externalUrl
          listOfContent {
            text
            content
          }
          fa_icon_name
          fa_icon_weight
          fa_icon_color
          listOfLinks {
            links {
              label
              icon {
                url
                webPURL
              }
              url
              alt
            }
          }
        }

        ... on ComponentElementsListOfLinks {
          links {
            label
            icon {
              url
              webPURL
            }
            url
          }
        }

        ... on ComponentElementsContent {
          content
        }

        ... on ComponentElementsMultiLevel {
          Level1 {
            Level1_title
            Level1_content
            ctas {
              label
              url
            }
            Level1_media {
              media {
                url
                alternativeText
                ext
                name
                webPURL
              }
            }
            Level2 {
              Level2_title
              Level2_content
              Level2_media {
                media {
                  url
                  webPURL
                }
                desktopImagePosition {
                  position
                }
              }
            }
          }
        }
      }
    }
  }
`;

@Injectable({
  providedIn: 'root',
})
export class FaultArticleGQL extends Query {
  document = FAULT_ARTICLE_QUERY;
}

@Injectable({
  providedIn: 'root',
})
export class FaultArticleService {
  constructor(
    private faultArticleGQL: FaultArticleGQL,
    private campaignContentService: CampaignContentService,
    @Inject(LIB_CONFIG) private config: LibConfig
  ) {}

  setArticle(id): void {
    const EXCLUDED_STRAPIUID = [
      'angular-care-priority-call',
      'angular-diagnostics-dynamic-tiles-failure',
      'angular_side_navigation',
    ];
    if (!ANALYTICS.STRAPIUID && !EXCLUDED_STRAPIUID.includes(id)) {
      ANALYTICS.STRAPIUID = id;
    }
  }

  getArticle(id): Observable<any> {
    if (id) {
      id = this.campaignContentService.getStrapiUID(id);
      this.setArticle(id);
      return this.faultArticleGQL
        .fetch({ uid: id, publicationState: this.config.previewDraftArticles ? 'PREVIEW' : 'LIVE' })
        .pipe(
          map((result: FaultArticlesResponse) => {
            return result?.data?.faultArticles[0];
          })
        );
    } else {
      return of({});
    }
  }
}
